import { useRef, useEffect } from 'react';

const SPACING = 14;
const DOT = 2;
const RADIUS = 130;
const REPEL = 55;
const SPRING = 0.07;
const DAMPING = 0.80;
const OP_REST = 0.18;
const OP_ACTIVE = 0.88;
const STATIC_STYLE = `rgba(255,255,255,${OP_REST})`;

function buildGrid(w, h) {
  const cols = Math.ceil(w / SPACING) + 1;
  const rows = Math.ceil(h / SPACING) + 1;
  const arr = new Array(cols * rows);
  let i = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      arr[i++] = { ox: c * SPACING, oy: r * SPACING, x: c * SPACING, y: r * SPACING, vx: 0, vy: 0, op: OP_REST, live: false };
    }
  }
  return arr;
}

export default function PixelRippleCanvas({ containerRef }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef?.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    let pixels = [];
    let mouse = null;
    let raf = null;

    const resize = () => {
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
      pixels = buildGrid(canvas.width, canvas.height);
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(container);

    const onMove = (e) => {
      const r = container.getBoundingClientRect();
      mouse = { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const onLeave = () => { mouse = null; };
    container.addEventListener('mousemove', onMove);
    container.addEventListener('mouseleave', onLeave);

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // --- physics pass ---
      for (let i = 0; i < pixels.length; i++) {
        const p = pixels[i];

        if (mouse) {
          const dx = p.ox - mouse.x;
          const dy = p.oy - mouse.y;
          if (Math.abs(dx) < RADIUS && Math.abs(dy) < RADIUS) {
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < RADIUS) {
              const t = 1 - dist / RADIUS;
              const ang = Math.atan2(dy, dx);
              const tx = p.ox + Math.cos(ang) * REPEL * t;
              const ty = p.oy + Math.sin(ang) * REPEL * t;
              p.vx += (tx - p.x) * SPRING;
              p.vy += (ty - p.y) * SPRING;
              p.op = OP_REST + (OP_ACTIVE - OP_REST) * t;
              p.live = true;
              continue;
            }
          }
        }

        if (p.live) {
          p.vx += (p.ox - p.x) * SPRING;
          p.vy += (p.oy - p.y) * SPRING;
          if (p.op > OP_REST) p.op -= 0.025;
        }
      }

      // --- update positions ---
      for (let i = 0; i < pixels.length; i++) {
        const p = pixels[i];
        if (!p.live) continue;
        p.vx *= DAMPING;
        p.vy *= DAMPING;
        p.x += p.vx;
        p.y += p.vy;
        if (
          Math.abs(p.vx) < 0.01 && Math.abs(p.vy) < 0.01 &&
          Math.abs(p.x - p.ox) < 0.3 && Math.abs(p.y - p.oy) < 0.3
        ) {
          p.x = p.ox; p.y = p.oy; p.vx = 0; p.vy = 0;
          p.op = OP_REST; p.live = false;
        }
      }

      // --- draw static dots in one batch ---
      ctx.fillStyle = STATIC_STYLE;
      for (let i = 0; i < pixels.length; i++) {
        if (!pixels[i].live) ctx.fillRect(pixels[i].ox, pixels[i].oy, DOT, DOT);
      }

      // --- draw live (displaced) dots on top ---
      for (let i = 0; i < pixels.length; i++) {
        const p = pixels[i];
        if (!p.live) continue;
        ctx.fillStyle = `rgba(255,255,255,${p.op.toFixed(3)})`;
        ctx.fillRect(Math.round(p.x), Math.round(p.y), DOT, DOT);
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      container.removeEventListener('mousemove', onMove);
      container.removeEventListener('mouseleave', onLeave);
    };
  }, [containerRef]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 z-[2]"
      style={{ imageRendering: 'pixelated' }}
    />
  );
}
